/**
 * useFeatureGate — hook for checking and displaying feature usage limits.
 *
 * Usage:
 *   const { checkGate, gateModal } = useFeatureGate();
 *
 *   const handleGenerate = async () => {
 *     if (await checkGate('cover_letter')) return; // blocked — modal shown
 *     // ... proceed with generation
 *   };
 *
 *   return <>{gateModal}</>; // render the modal somewhere in JSX
 */
import { useState, useCallback } from 'react';
import axios from 'axios';
import { API } from './apiBase';
import FeatureGateModal from '../components/FeatureGateModal';

export function useFeatureGate() {
  const [gateState, setGateState] = useState(null); // null = closed

  /**
   * Call before performing a gated action.
   * Returns true if BLOCKED (modal shown), false if allowed.
   */
  const checkGate = useCallback(async (feature) => {
    try {
      const res = await axios.get(`${API}/feature-usage`, { withCredentials: true });
      const info = res.data?.features?.[feature];
      if (!info) return false; // unknown feature → allow
      if (info.limit === -1) return false; // unlimited
      if (info.used < info.limit) return false; // under limit

      // Over limit — show modal
      setGateState({
        feature,
        used: info.used,
        limit: info.limit,
        period: info.period,
      });
      return true; // blocked
    } catch (err) {
      // If the check fails (network, auth), allow the action.
      // The backend gate will catch it with a 403 if truly over limit.
      if (err?.response?.status === 403) {
        const detail = err.response.data?.detail;
        if (detail?.code === 'feature_limit_reached') {
          setGateState({
            feature: detail.feature || feature,
            used: detail.used || 0,
            limit: detail.limit || 0,
            period: detail.period || 'month',
          });
          return true;
        }
      }
      return false;
    }
  }, []);

  const closeGate = useCallback(() => setGateState(null), []);

  const gateModal = gateState ? (
    <FeatureGateModal
      open={true}
      onClose={closeGate}
      feature={gateState.feature}
      used={gateState.used}
      limit={gateState.limit}
      period={gateState.period}
    />
  ) : null;

  return { checkGate, gateModal };
}
